import ContentModuleRenderer from "@components/ContentModuleRenderer";
import QuickExitButton from "@components/QuickExitButton";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { withAllSDKmarketingSite } from "@contexts/withAllSDK";
import { isEmptyContentfulEntry } from "@helpers/contentful";
import { getBaseUrl } from "@helpers/env";
import usePreventImageDownload from "@hooks/usePreventImageDownload";
import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";
import type {
	IMarketingSiteCombinedFields,
	IPageSeo,
	IPageSeoFields,
	IWebsiteFields,
} from "@types-contentful";
import ContentService from "@utils/content-service";
import { PreviewContentService } from "@utils/content-service";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import React, { useEffect } from "react";
import { useRouter } from "next/router";

interface Props {
	page: IMarketingSiteCombinedFields;
	pageMeta?: IPageSeoFields;
}

const Page: NextPage<Props> = ({ page }) => {
	const router = useRouter();
	const currentPath = router.asPath.split("?")[0];
	const isDomesticViolencePage = currentPath === "/domestic-violence";
	const pageWithUpdates = useContentfulLiveUpdates(page);

	const updatedPage = pageWithUpdates;
	const { modules, pageMeta } = updatedPage;

	// Type guard to ensure pageMeta is IPageSeo (not IVariationContainer)
	const isPageSeo = (meta: any): meta is IPageSeo => {
		return (
			meta &&
			meta.sys &&
			meta.sys.contentType &&
			meta.sys.contentType.sys &&
			meta.sys.contentType.sys.id === "pageSeo"
		);
	};

	const seoFields = isPageSeo(pageMeta) ? pageMeta.fields : undefined;

	const {
		title,
		description,
		ogtitle,
		ogdescription,
		ogimageMedia,
		keywords,
		author,
		ogurl,
		oglocale,
		ogimage,
		ogtype,
		noindex,
		pageSchema,
	} = seoFields || {};

	const getOgImageUrl = (url?: string | null) => {
		if (!url) return url ?? undefined;

		if (url.startsWith("//")) {
			return `https:${url}`;
		}

		if (url.startsWith("/")) {
			return `${getBaseUrl()}${url}`;
		}

		return url;
	};

	const noIndexUrls = [
		"/terms-policies/complaints-policy",
		"/terms-policies/privacy",
	];
	const isNoIndex =
		(ogurl && noIndexUrls.includes(ogurl)) || !seoFields || !!noindex;
	usePreventImageDownload();

	// Organization schema markup
	const organizationSchema = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: "felix mobile",
		url: "https://felixmobile.com.au",
		logo: "https://felixmobile.com.au/images/felixmobile_Logo_RGB_Secondary.svg",
		alternateName: "felix",
		parentOrganization: "TPG Telecom",
		description:
			"felix mobile offers three great value, no lock-in mobile plans with unlimited standard calls and texts for use in Australia, with one tree planted for every month you're with us. Starting from only $25/month. Find out more. T+C apply.",
		sameAs: [
			"https://www.tiktok.com/@felixmobileau",
			"https://www.linkedin.com/company/felixmobileau/",
			"https://www.facebook.com/felixmobileau",
			"https://www.instagram.com/felixmobileau/",
			"https://www.youtube.com/channel/UCHY9f8xxeZS0fgJZo10UGpA",
		],
		contactPoint: [
			{
				"@type": "ContactPoint",
				contactType: "customer service",
				areaServed: "AU",
				availableLanguage: "en",
			},
		],
	};

	// SoftwareApplication schema markup for app page
	const appSchema = {
		"@context": "https://schema.org",
		"@type": "SoftwareApplication",
		name: "Felix Mobile App",
		operatingSystem: "iOS, Android",
		applicationCategory: "UtilitiesApplication",
		aggregateRating: {
			"@type": "AggregateRating",
			ratingValue: 4.5,
			ratingCount: 1200,
		},
		offers: {
			"@type": "Offer",
			price: 0,
			priceCurrency: "AUD",
		},
	};

	// Google Schema markup for plan page.
	const planPageSchema = {
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "Organization",
				"@id": "https://felixmobile.com.au/#organization",
				name: "Felix Mobile",
				url: "https://felixmobile.com.au",
				logo: "https://felixmobile.com.au/assets/logo.svg",
				sameAs: [
					"https://www.facebook.com/felixmobileau",
					"https://www.instagram.com/felixmobileau",
				],
				contactPoint: {
					"@type": "ContactPoint",
					contactType: "customer service",
					areaServed: "AU",
					availableLanguage: ["English"],
				},
			},
			{
				"@type": "Product",
				"@id": "https://felixmobile.com.au/plan",
				name: "Felix Mobile SIM-Only Plans",
				description:
					"Flexible, no lock-in SIM-only mobile plans with unlimited talk & text, data banking, and 5G/4G access. Powered by 100% renewable energy and carbon neutral certified.",
				brand: {
					"@id": "https://felixmobile.com.au/#organization",
				},
				url: "https://felixmobile.com.au/plan",
				offers: [
					{
						"@type": "Offer",
						name: "25GB Mobile Plan",
						url: "https://felixmobile.com.au/plan/25GB-mobile-plan",
						priceCurrency: "AUD",
						price: "30.00",
						itemCondition: "https://schema.org/NewCondition",
						availability: "https://schema.org/InStock",
						description:
							"25GB data with unlimited talk & text, data banking, and 5G/4G access. Speeds capped at 150Mbps. No lock-in contract.",
						seller: {
							"@id": "https://felixmobile.com.au/#organization",
						},
					},
					{
						"@type": "Offer",
						name: "50GB Mobile Plan",
						url: "https://felixmobile.com.au/plan/50GB-mobile-plan",
						priceCurrency: "AUD",
						price: "35.00",
						itemCondition: "https://schema.org/NewCondition",
						availability: "https://schema.org/InStock",
						description:
							"50GB data with unlimited talk & text, data banking, and 5G/4G access. Speeds capped at 150Mbps. No lock-in contract.",
						seller: {
							"@id": "https://felixmobile.com.au/#organization",
						},
					},
					{
						"@type": "Offer",
						name: "Unlimited Data Mobile Plan",
						url: "https://felixmobile.com.au/plan/unlimited-data-mobile-plan",
						priceCurrency: "AUD",
						price: "40.00",
						itemCondition: "https://schema.org/NewCondition",
						availability: "https://schema.org/InStock",
						description:
							"Unlimited data at speeds up to 20Mbps with unlimited talk & text. 5G/4G access. No lock-in contract.",
						seller: {
							"@id": "https://felixmobile.com.au/#organization",
						},
					},
				],
			},
		],
	};

	// Check if current page is the plan page
	const isPlanPage = currentPath === "/plan";

	// Check if current page is homepage (empty slug), about-us page, or app page
	const isHomepage = router.asPath === "/" || router.asPath === "";
	const isAboutUsPage = router.asPath === "/about-us";
	const isAppPage = router.asPath === "/app";

	// History trap for /plan page after quick exit (TPG/iiNet approach)
	// When user lands on /plan after quick exit, create history trap that keeps them on /plan
	useEffect(() => {
		if (!isPlanPage || typeof window === "undefined") return;

		// Check if we came from quick exit by checking history state
		// The quick exit button sets quickExit: true in the history state
		const currentState = window.history.state;
		const hasQuickExitFlag = currentState?.quickExit === true;

		if (hasQuickExitFlag) {
			// Intercept back button navigation
			// Track back clicks to keep user on /plan for 8 clicks
			let backClickCount = 0;
			const handlePopState = () => {
				backClickCount++;

				// For the first 8 back clicks, keep user on /plan
				// This handles the 8 history entries we injected
				if (backClickCount <= 8) {
					const currentPath = window.location.pathname;

					// If not on /plan, redirect to clean /plan
					if (currentPath !== "/plan") {
						try {
							// Replace current entry with clean /plan
							window.history.replaceState({ trap: true }, "", "/plan");
							// Push another /plan entry to maintain the trap
							window.history.pushState({ trap: true }, "", "/plan");
						} catch {
							// If that fails, just navigate to /plan
							window.location.replace("/plan");
						}
					} else {
						// Already on /plan, just push another entry to maintain trap
						try {
							window.history.pushState({ trap: true }, "", "/plan");
						} catch {
							// Ignore
						}
					}
				}
				// After 8 back clicks, allow normal navigation (to previous page before DV page)
			};

			window.addEventListener("popstate", handlePopState);

			return () => {
				window.removeEventListener("popstate", handlePopState);
			};
		}
	}, [isPlanPage]);

	return (
		<>
			<Head>
				<GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_KEY || ""} />

				{/* Console ownership tag for homepage only */}
				{isHomepage && (
					<meta
						name="google-site-verification"
						content="iY21H9r1m30kxm3XuIoz2q6TfGjwmiSLjUiHj7mlXTc"
					/>
				)}

				{/* Organization schema markup for homepage and about-us page */}
				{(isHomepage || isAboutUsPage) && (
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(organizationSchema),
						}}
					/>
				)}

				{!isEmptyContentfulEntry(pageSchema) && (
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(pageSchema, null, 2),
						}}
					/>
				)}

				{isAppPage && !pageSchema && (
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(appSchema),
						}}
					/>
				)}

				{/* Google Schema markup for plan page */}
				{isPlanPage && (
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(planPageSchema),
						}}
					/>
				)}

				<title>{isEmptyContentfulEntry(title) ? "" : title}</title>
				<link rel="canonical" href={`${getBaseUrl()}${currentPath}`} />
				<meta
					name="facebook-domain-verification"
					content="umwvevf3vuew9ebyg5bb4hxqswovn9"
				/>
				{!isEmptyContentfulEntry(author) && (
					<meta name="author" content={author} />
				)}
				{!isEmptyContentfulEntry(description) && (
					<meta name="description" content={description} />
				)}
				{!isEmptyContentfulEntry(keywords) && (
					<meta name="keywords" content={keywords} />
				)}

				{!isEmptyContentfulEntry(ogtitle) && (
					<meta property="og:title" content={ogtitle} />
				)}
				{!isEmptyContentfulEntry(ogdescription) && (
					<meta property="og:description" content={ogdescription} />
				)}
				{ogurl && !isEmptyContentfulEntry(ogurl) && (
					<meta property="og:url" content={ogurl} />
				)}
				{!isEmptyContentfulEntry(oglocale) && (
					<meta property="og:locale" content={oglocale} />
				)}
				{!isEmptyContentfulEntry(
					ogimageMedia?.fields?.file?.url || ogimage,
				) && (
					<meta
						property="og:image"
						content={getOgImageUrl(ogimageMedia?.fields?.file?.url || ogimage)}
					/>
				)}
				{!isEmptyContentfulEntry(ogtype) && (
					<meta property="og:type" content={ogtype} />
				)}

				{isNoIndex && <meta name="robots" content="noindex" />}

				{isDomesticViolencePage && (
					<>
						<meta name="referrer" content="no-referrer" />
						<link rel="preconnect" href="https://www.abc.net.au" />
						<link rel="dns-prefetch" href="https://www.abc.net.au" />
					</>
				)}
			</Head>

			{isDomesticViolencePage && (
				<QuickExitButton
					className="fixed top-4 right-4 z-50"
					safeInternalPath="/"
					redirectUrl="https://felixmobile.com.au/plan"
				/>
			)}

			{modules.map((module) => (
				<React.Fragment key={module?.sys?.id}>
					<ContentModuleRenderer
						module={module}
						//@ts-ignore
						id={module?.sys?.id}
					/>
				</React.Fragment>
			))}
		</>
	);
};

export default withAllSDKmarketingSite(Page);

export const getStaticPaths: GetStaticPaths = async () => {
	function removeSlash(param: string) {
		if (param.charAt(0) === "/") {
			return param.slice(1).trim();
		}
		return param;
	}

	const marketingSiteCombined =
		await ContentService.instance.getEntryById<IWebsiteFields>(
			`${process.env.NEXT_PUBLIC_CONTENTFUL_WEBSITE_ID}`,
		);

	// Log pages without relativeUrl or that are drafts for debugging
	const pagesWithoutUrl = marketingSiteCombined.fields.pages.filter(
		(page: any) => !page?.fields?.relativeUrl,
	);
	const draftPages = marketingSiteCombined.fields.pages.filter(
		(page: any) => !page.sys.publishedVersion,
	);

	// Only show debugging logs in development or when explicitly enabled
	if (
		process.env.NODE_ENV === "development" ||
		process.env.NEXT_PUBLIC_DEBUG_CONTENTFUL
	) {
		if (pagesWithoutUrl.length > 0) {
			console.warn(
				`Found ${pagesWithoutUrl.length} Contentful pages without relativeUrl:`,
				pagesWithoutUrl.map((page: any) => ({
					id: page.sys.id,
					name: page.fields?.name || "Unnamed page",
					isPublished: !!page.sys.publishedVersion,
				})),
			);
		}

		if (draftPages.length > 0) {
			console.warn(
				`Found ${draftPages.length} draft Contentful pages (these will be excluded from build):`,
				draftPages.map((page: any) => ({
					id: page.sys.id,
					name: page.fields?.name || "Unnamed page",
					relativeUrl: page.fields?.relativeUrl || "No URL",
				})),
			);
		}
	}

	const paths = marketingSiteCombined.fields.pages
		.filter((page: any) => {
			// Only include published pages with relativeUrl
			return page?.fields?.relativeUrl && page.sys.publishedVersion;
		})
		.map((page: any) => ({
			params: {
				slug: removeSlash(page.fields.relativeUrl)
					.split("/")
					.filter((segment) => segment !== ""),
			},
		}))
		.filter((path) => {
			const firstSlug = path.params.slug[0];
			return firstSlug !== "404" && firstSlug !== "500";
		});

	// Add the homepage to the paths array
	paths.push({ params: { slug: [] } });

	return {
		paths,
		fallback: "blocking",
	};
};
export const getStaticProps: GetStaticProps<
	Props,
	{ slug: Array<string> }
> = async (ctx) => {
	function removeSlash(param: string) {
		if (param.charAt(0) === "/") {
			return param.slice(1);
		}
		return param;
	}

	const { slug = [] } = ctx.params || {};
	const slugStr = slug.length ? slug.join("/") : "";

	// Check if the slug contains "undefined" - this can happen with draft pages or malformed URLs
	if (slugStr.includes("undefined")) {
		return { notFound: true };
	}

	const client = ctx.preview ? PreviewContentService : ContentService;

	const marketingSiteCombined =
		await client.instance.getEntryById<IWebsiteFields>(
			`${process.env.NEXT_PUBLIC_CONTENTFUL_WEBSITE_ID}`,
		);

	const { logo, navigation, notificationBar, notificationBarToggle, pages } =
		marketingSiteCombined.fields;

	const page = pages.find((pageEntry: any) => {
		if (
			!pageEntry ||
			!pageEntry.fields ||
			!pageEntry.fields.relativeUrl ||
			!pageEntry.sys.publishedVersion
		) {
			return false;
		}
		const pageUrl = removeSlash(pageEntry.fields.relativeUrl).trim();

		// Normalize both URLs: lowercase and replace underscores with hyphens
		const normalizedPageUrl = pageUrl.toLowerCase().replace(/_/g, "-");
		const normalizedSlugStr = slugStr.toLowerCase().replace(/_/g, "-");

		return normalizedPageUrl === normalizedSlugStr;
	});

	if (!page) {
		return { notFound: true };
	}

	const pageFields = { ...page.fields };

	return {
		props: {
			page: pageFields,
			logo,
			navigation,
			notificationBar,
			notificationBarToggle,
		},
	};
};
